// Rama publiczna paczki 37 — nagłówek (artboardy 01, 01b–01f, 02, 03a–03c).
//
// 64 px (R3; było 72), logo NA SIATCE STRONY (x = 100 przy 1440, bo kontener ma
// 1240 px + 2 × 44), i cztery rzeczy po prawej: „Cennik”, „FAQ”, PL/EN,
// „Zaloguj się”, „Zacznij za darmo”. „Funkcje” i „Jak to działa” odeszły razem
// z sekcjami, do których prowadziły (decyzja właściciela, brief paczki 37).
//
// PRZYKLEJONY PO PRZEWINIĘCIU (R3, artboard 01d): po zjechaniu z góry strony pasek
// staje się `fixed` z PEŁNYM tłem, linią 1 px i BEZ cienia. Nie `position: sticky`,
// bo scena strony głównej ma `overflow: hidden` (warstwy światła), a sticky w takim
// rodzicu nie klei się w ogóle. Miejsce w układzie trzyma `.mp-nav-slot` o stałej
// wysokości, więc przejście w `fixed` niczego nie podnosi.
//
// STAN AKTYWNY MA DWA NOŚNIKI (reguła 27): podkreślenie 2 px + grubość 600, nigdy sam
// kolor — plus `aria-current="page"`, bo czytnik ekranu nie widzi ani jednego, ani
// drugiego.
//
// 390 (R2, rozstrzygnięcie 2): pierwszy rząd to logo + „Zacznij za darmo” (44 px) — na
// KAŻDEJ stronie publicznej, jawnie, nie przez ukrycie przycisku; „Cennik”, „FAQ”
// i „Zaloguj się” stoją w drugim rzędzie, a PL/EN schodzi do stopki. Drugi rząd żyje
// TYLKO na górze strony: po przyklejeniu zostaje logo i akcja (artboard 01d, 01e).
// Żadnego menu pod hamburgerem: trzy pozycje mieszczą się w rzędzie 44 px.
//
// Sesja rozpoznana: JEDNA akcja „Przejdź do editów” zamiast pary (MAPA, dopiski do
// tabel) — gość i klient nie dostają tej samej propozycji.

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { useAuthOptional } from '../../auth/AuthContext'
import LangSwitcher from '../LangSwitcher'
import Logo from '../Logo'
import { useT } from '../../lib/i18n'
import { usePublicPath } from '../../lib/publicPaths'
import { Button } from '../../ds/primitives/Button'
import '../../styles/minimal-public.css'

export interface PublicHeaderProps {
  /** Która pozycja jest bieżącą stroną. Strona główna nie ma własnej pozycji. */
  active?: 'pricing' | 'faq'
  /** Nagłówek stoi na ciemnej scenie (strona główna): główna akcja jest z obrysem. */
  onStage?: boolean
  /**
   * Próg przyklejenia w px przewinięcia, czytany przy każdym przewinięciu. Strona główna podaje
   * koniec pasa przypięcia hero (paczka HD): w przypięciu nagłówek wygląda jak przy p = 0.
   * Bez propu: `PUBLIC_HEADER_H`, jak na każdej innej stronie.
   */
  stuckFrom?: () => number
  /** `false`: ekran ma własną wypełnioną akcję tej samej drogi (404 z sesją, VA-14). */
  cta?: boolean
  /**
   * Na ekranie stoi wypełniony przycisk tej samej akcji (hero, sekcja zamykająca): akcja
   * nagłówka zostaje z obrysem także po przyklejeniu (VB-10, jeden pełny przycisk).
   */
  quietCta?: boolean
}

/**
 * Czy strona jest już przewinięta z samej góry.
 *
 * Próg to wysokość nagłówka, a nie zero: pasek ma się przykleić dopiero wtedy, gdy
 * naprawdę wyszedł z widoku, a nie drgnąć przy pierwszym pikselu kółka myszy. Strona
 * z przypiętym hero podaje własny próg (koniec pasa), czytany w każdej klatce.
 */
function useScrolledPast(threshold: () => number): boolean {
  const [past, setPast] = useState(false)
  const read = useRef(threshold)
  read.current = threshold
  useEffect(() => {
    if (typeof window === 'undefined') return
    let frame = 0
    const update = () => { frame = 0; setPast(window.scrollY > read.current()) }
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update) }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => { window.removeEventListener('scroll', onScroll); if (frame) cancelAnimationFrame(frame) }
  }, [])
  return past
}

/** Wysokość przyklejonego paska (R3, „Wymiary”). */
export const PUBLIC_HEADER_H = 64

const headerHeight = () => PUBLIC_HEADER_H

export default function PublicHeader({ active, onStage = false, stuckFrom = headerHeight, cta = true, quietCta = false }: PublicHeaderProps) {
  const t = useT()
  const pub = usePublicPath()
  // Opcjonalnie: 404 gościa renderuje się poza <AuthProvider> (LocaleRoute i testy),
  // a nagłówek, który tam rzuca, kładzie całą stronę.
  const user = useAuthOptional()?.user ?? null
  const stuck = useScrolledPast(stuckFrom)
  const variant = (onStage && !stuck) || quietCta ? 'secondary' : 'primary'

  const link = (to: string, key: 'pricing' | 'faq', label: string) => (
    <Link to={to} className={`mp-nav-link${active === key ? ' is-active' : ''}`}
          aria-current={active === key ? 'page' : undefined}>{label}</Link>
  )

  return (
    <div className={`mp-nav-slot${onStage ? ' is-on-stage' : ''}`}>
      <header className={`mp-nav${stuck ? ' is-stuck' : ''}`}>
        <Link to={pub()} className="logo" aria-label="EDIMI"><Logo /></Link>
        {link(pub('/pricing'), 'pricing', t('landing.nav.cennik'))}
        {link(pub('/faq'), 'faq', t('landing.nav.faq'))}
        <LangSwitcher variant="segments" />
        {user ? null : <Link to="/login" className="mp-nav-link mp-nav-login">{t('landing.nav.login')}</Link>}
        <i className="mp-nav-break" aria-hidden="true" />
        {!cta ? null : user ? (
          <Button to="/edits" variant={variant} data-cta="naglowek">
            {t('landing.nav.toPanel')}
          </Button>
        ) : (
          <Button to="/register" variant={variant} data-cta="naglowek">
            {t('landing.nav.startFree')}
          </Button>
        )}
      </header>
    </div>
  )
}
